import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Checkout from '../Components/Checkout';
import FollowButton from '../Components/FollowButton';
import { app } from '../firebase';
import { hasPlayableAudio, normalizeTrackDocument } from '../utils/tracks';
import './ArtistProfile.css';

const db = getFirestore(app);
const auth = getAuth(app);

const ArtistProfile = () => {
    const { id } = useParams();
    const [artist, setArtist] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentUserFollowing, setCurrentUserFollowing] = useState([]);
    const [playingTrack, setPlayingTrack] = useState(null);
    const [audioRef, setAudioRef] = useState(null);
    const [checkoutTrack, setCheckoutTrack] = useState(null);
    const [user, setUser] = useState(() => auth.currentUser);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(setUser);
        return unsubscribe;
    }, []);

    useEffect(() => {
        const fetchCurrentUserFollowing = async () => {
            if (!user) {
                setCurrentUserFollowing([]);
                return;
            }
            try {
                const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
                if (profileDoc.exists()) {
                    const following = profileDoc.data().following || [];
                    setCurrentUserFollowing(Array.isArray(following) ? following : []);
                }
            } catch (err) {
                console.error('Error fetching following list:', err);
            }
        };

        fetchCurrentUserFollowing();
    }, [user]);

    useEffect(() => {
        const fetchArtist = async () => {
            try {
                setLoading(true);
                setError('');
                
                // Fetch profile from Firestore
                const profileDoc = await getDoc(doc(db, 'profiles', id));
                if (profileDoc.exists()) {
                    const profileData = profileDoc.data();
                    const profile = profileData.profile || {};
                    const followersList = Array.isArray(profileData.followers) ? profileData.followers : [];
                    const followingList = Array.isArray(profileData.following) ? profileData.following : [];
                    
                    setArtist({
                        id: id,
                        name: profile.name || 'Unknown Artist',
                        bio: profile.bio || '',
                        profilePicture: profile.profilePicture || '',
                        genre: profile.genre || 'Uncategorized',
                        socialLinks: profile.socialLinks || { twitter: '', instagram: '', facebook: '' },
                    });
                    setFollowers(followersList);
                    setFollowing(followingList);
                } else {
                    setError('Artist not found');
                    setArtist(null);
                }

                // Fetch tracks by this artist
                const tracksQuery = query(collection(db, 'tracks'), where('userId', '==', id));
                const tracksSnapshot = await getDocs(tracksQuery);
                const fetchedTracks = tracksSnapshot.docs
                    .map(normalizeTrackDocument)
                    .filter(hasPlayableAudio);
                setTracks(fetchedTracks);
            } catch (err) {
                console.error('Error fetching artist:', err);
                setError('Failed to load artist profile.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchArtist();
        }
    }, [id]);

    useEffect(() => {
        if (artist) {
            document.title = `${artist.name} - Artist Profile | Tune`;
        } else {
            document.title = 'Artist Profile | Tune';
        }
    }, [artist]);

    const handlePlayPreview = (track, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (playingTrack?.id === track.id) {
            // Stop playing
            if (audioRef) {
                audioRef.pause();
                audioRef.currentTime = 0;
            }
            setPlayingTrack(null);
            setAudioRef(null);
        } else {
            // Play new track
            if (audioRef) {
                audioRef.pause();
            }
            setPlayingTrack(track);
        }
    };

    const handleAudioTimeUpdate = (e) => {
        // Limit preview to 30 seconds
        if (e.target.currentTime >= 30) {
            e.target.pause();
            e.target.currentTime = 0;
            setPlayingTrack(null);
            setAudioRef(null);
        }
    };

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef) {
                audioRef.pause();
                audioRef.currentTime = 0;
            }
        };
    }, [audioRef]);

    if (loading) {
        return (
            <div className="artist-profile">
                <div className="artist-profile-container">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading artist profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !artist) {
        return (
            <div className="artist-profile">
                <div className="artist-profile-container">
                    <div className="empty-state">
                        <h3>{error || 'Artist not found'}</h3>
                        <p>The artist you're looking for doesn't exist.</p>
                        <Link to="/marketplace" style={{ color: '#c98516', textDecoration: 'underline', marginTop: '1rem', display: 'inline-block' }}>
                            Browse Marketplace
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="artist-profile">
            <div className="artist-profile-container">
                <div className="artist-profile-header">
                    <div className="artist-profile-avatar">
                        {artist.profilePicture ? (
                            <img src={artist.profilePicture} alt={artist.name} />
                        ) : (
                            <div className="avatar-placeholder">
                                {artist.name.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                    <h1 className="artist-profile-name">{artist.name}</h1>
                    {artist.bio && <p className="artist-profile-bio">{artist.bio}</p>}
                    
                    {/* Social Links */}
                    {artist.socialLinks && (artist.socialLinks.twitter || artist.socialLinks.instagram || artist.socialLinks.facebook) && (
                        <div className="artist-social-links">
                            {artist.socialLinks.twitter && (
                                <a href={artist.socialLinks.twitter.startsWith('http') ? artist.socialLinks.twitter : `https://twitter.com/${artist.socialLinks.twitter}`} target="_blank" rel="noopener noreferrer" className="social-link" title="Twitter">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                                    </svg>
                                </a>
                            )}
                            {artist.socialLinks.instagram && (
                                <a href={artist.socialLinks.instagram.startsWith('http') ? artist.socialLinks.instagram : `https://instagram.com/${artist.socialLinks.instagram}`} target="_blank" rel="noopener noreferrer" className="social-link" title="Instagram">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                                    </svg>
                                </a>
                            )}
                            {artist.socialLinks.facebook && (
                                <a href={artist.socialLinks.facebook.startsWith('http') ? artist.socialLinks.facebook : `https://facebook.com/${artist.socialLinks.facebook}`} target="_blank" rel="noopener noreferrer" className="social-link" title="Facebook">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                                    </svg>
                                </a>
                            )}
                        </div>
                    )}
                    
                    {/* Follow Button */}
                    {user && user.uid !== id && (
                        <div className="artist-follow-button-wrapper">
                            <FollowButton
                                targetUserId={id}
                                targetUserName={artist.name}
                                currentUserFollowing={currentUserFollowing}
                                onFollowChange={(userId, isFollowing) => {
                                    if (isFollowing) {
                                        setCurrentUserFollowing(prev => [...prev, userId]);
                                        setFollowers(prev => [...prev, user.uid]);
                                    } else {
                                        setCurrentUserFollowing(prev => prev.filter(uid => uid !== userId));
                                        setFollowers(prev => prev.filter(uid => uid !== user.uid));
                                    }
                                }}
                            />
                        </div>
                    )}
                    
                    {artist.genre && <span className="artist-profile-genre">{artist.genre}</span>}
                    
                    <div className="artist-profile-stats">
                        <div className="artist-stat">
                            <div className="artist-stat-value">{tracks.length}</div>
                            <div className="artist-stat-label">Tracks</div>
                        </div>
                        <div className="artist-stat">
                            <div className="artist-stat-value">{followers.length}</div>
                            <div className="artist-stat-label">Followers</div>
                        </div>
                        <div className="artist-stat">
                            <div className="artist-stat-value">{following.length}</div>
                            <div className="artist-stat-label">Following</div>
                        </div>
                    </div>
                </div>

                <div className="artist-tracks-section">
                    <div className="section-header">
                        <h2 className="section-title">All Tracks</h2>
                        {tracks.length > 0 && (
                            <span className="tracks-count">{tracks.length} {tracks.length === 1 ? 'track' : 'tracks'}</span>
                        )}
                    </div>
                    
                    {tracks.length === 0 ? (
                        <div className="empty-state">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M9 18V5l12-2v13"/>
                                <circle cx="6" cy="18" r="3"/>
                                <circle cx="18" cy="16" r="3"/>
                            </svg>
                            <h3>No tracks yet</h3>
                            <p>This artist hasn't uploaded any tracks yet.</p>
                        </div>
                    ) : (
                        <div className="artist-tracks-grid">
                            {tracks.map((track) => (
                                <div key={track.id} className="artist-track-card">
                                    <div className="track-card-artwork">
                                        {track.coverImage ? (
                                            <img src={track.coverImage} alt={track.title} />
                                        ) : (
                                            <div className="track-artwork-placeholder"></div>
                                        )}
                                        <div className="play-overlay">
                                            <button 
                                                className="play-button"
                                                onClick={(e) => handlePlayPreview(track, e)}
                                            >
                                                {playingTrack?.id === track.id ? (
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                        <rect x="6" y="4" width="4" height="16"/>
                                                        <rect x="14" y="4" width="4" height="16"/>
                                                    </svg>
                                                ) : (
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M8 5v14l11-7z"/>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="track-card-info">
                                        <h3 className="track-card-title">{track.title || 'Untitled'}</h3>
                                        <div className="track-card-meta">
                                            <span className="track-genre-badge">{track.genre || 'Uncategorized'}</span>
                                            <span className="track-price-badge">${track.price ? (typeof track.price === 'number' ? track.price.toFixed(2) : parseFloat(track.price).toFixed(2)) : '0.00'}</span>
                                        </div>
                                        {user && user.uid !== track.userId && (
                                            <button
                                                className="buy-now-btn-small"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (!user) {
                                                        alert('Please log in to purchase tracks.');
                                                        return;
                                                    }
                                                    setCheckoutTrack(track);
                                                }}
                                            >
                                                Buy Now
                                            </button>
                                        )}
                                    </div>
                                    {playingTrack?.id === track.id && track.fileUrl && (
                                        <audio
                                            ref={(ref) => setAudioRef(ref)}
                                            src={track.fileUrl}
                                            autoPlay
                                            onTimeUpdate={handleAudioTimeUpdate}
                                            onEnded={() => setPlayingTrack(null)}
                                            style={{ display: 'none' }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Checkout Modal */}
            {checkoutTrack && (
                <Checkout
                    track={checkoutTrack}
                    onSuccess={() => {
                        setCheckoutTrack(null);
                    }}
                    onCancel={() => setCheckoutTrack(null)}
                />
            )}
        </div>
    );
};

export default ArtistProfile;

